import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";
import { isEqualWith } from "lodash";

import { DeleteLiabilityWaiver, GetLiabilityWaivers, PutLiabilityWaiver } from "api/rpc/2024-04/facilityAdmin/facility/liabilityWaiver";
import { StatusCode } from "api/protocols";
import Page from "components/page/Page";
import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import TextEditor from "components/textEditor/textEditor";
import Popup from "components/popup/Popup";
import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";

interface ILiabilityWaiverEditState {
  id: number;
  title: string;
  content: string;
}

export default function LiabilityWaiverEdit() {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();

  const [waiverState, setWaiverState] = useState<ILiabilityWaiverEditState>({
    id: Number(id),
    title: "",
    content: "",
  });

  const [waiverStateBeforeChanges, setWaiverStateBeforeChanges] = useState<ILiabilityWaiverEditState>(undefined);
  const [waiverStateLoaded, setWaiverStateLoaded] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const loadWaiver = useCallback(async () => {
    const waiverId = Number(id);
    if (!waiverId || Number.isNaN(waiverId)) {
      dispatch(showError(t("Invalid liability waiver id")));
      history.push("/admin/settings/liability-waivers");
      return;
    }

    const res = await GetLiabilityWaivers({ id: waiverId }, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading liability waiver")));
      history.push("/admin/settings/liability-waivers");
      return;
    }

    const current = Array.isArray(res.data) ? res.data[0] : undefined;
    if (!current) {
      dispatch(showError(t("Liability waiver not found")));
      history.push("/admin/settings/liability-waivers");
      return;
    }

    const nextState: ILiabilityWaiverEditState = {
      id: current.id,
      title: current.title ?? "",
      content: current.content ?? "",
    };

    setWaiverState(nextState);
    setWaiverStateLoaded(true);
  }, [dispatch, history, id, t]);

  useEffect(() => {
    void loadWaiver();
  }, [loadWaiver]);

  async function saveLiabilityWaiver() {
    const res = await PutLiabilityWaiver(
      {
        id: waiverState.id,
        title: waiverState.title,
        content: waiverState.content,
      },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error saving liability waiver")));
      return;
    }

    dispatch(showSuccess(t("Liability waiver saved")));
    setWaiverStateBeforeChanges(waiverState);
    history.push("/admin/settings/liability-waivers");
  }

  async function deleteLiabilityWaiver() {
    setDeleteModalOpen(false);
    const res = await DeleteLiabilityWaiver({ id: waiverState.id }, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting liability waiver")));
      return;
    }

    dispatch(showSuccess(t("Liability waiver deleted")));
    history.push("/admin/settings/liability-waivers");
  }

  function unsavedChangesExist() {
    if (waiverStateBeforeChanges === undefined) {
      if (waiverStateLoaded) {
        setWaiverStateBeforeChanges(waiverState);
      }
      return false;
    }

    return !isEqualWith(waiverStateBeforeChanges, waiverState);
  }

  function cancelUnsavedChanges() {
    setWaiverState(waiverStateBeforeChanges);
  }

  function setWaiverContent(content: string) {
    setWaiverState(prev => ({ ...prev, content }));
  }

  return (
    <Page
      title={t("Edit Liability Waiver")}
      narrow
      primaryAction={{
        content: t("Delete"),
        action: () => setDeleteModalOpen(true),
        disabled: !waiverStateLoaded,
      }}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Liability Waivers"),
          url: "/admin/settings/liability-waivers",
        },
      ]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: saveLiabilityWaiver,
        onCancel: cancelUnsavedChanges,
      }}
    >
      <Card>
        <Card.Section>
          <Form>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  label={t("Title")}
                  value={waiverState.title}
                  onChange={e => setWaiverState(prev => ({ ...prev, title: e.target.value }))}
                />
              </FormLayout.Group>
            </FormLayout>
          </Form>
        </Card.Section>

        <Card.Section>
          <TextEditor markdownText={waiverState.content} markdownTextOnChange={setWaiverContent} />
        </Card.Section>
      </Card>

      <Popup
        open={deleteModalOpen}
        type="warning"
        title={t("Delete liability waiver?")}
        description={t("Are you sure you want to delete this liability waiver? This is permanent.")}
        okText={t("Delete")}
        onOk={deleteLiabilityWaiver}
        cancelText={t("Cancel")}
        onCancel={() => setDeleteModalOpen(false)}
      />
    </Page>
  );
}

